import styled, { css } from 'styled-components';
import {
  EMPTY_STATE_ALIGN_CENTER,
  EMPTY_STATE_SIZE_TOKENS,
  EMPTY_STATE_SIZE_XS,
  type EmptyStateAlign,
  type EmptyStateSize,
} from './EmptyState.constants';

interface RootProps {
  $align: EmptyStateAlign;
  $size: EmptyStateSize;
}

export const Root = styled.div<RootProps>`
  display: flex;
  flex-direction: column;
  ${({ $size }) => {
    const tokens = EMPTY_STATE_SIZE_TOKENS[$size];
    return css`
      gap: ${tokens.gap};
      padding: ${tokens.padY} 16px;
    `;
  }}
  ${({ $align }) =>
    $align === EMPTY_STATE_ALIGN_CENTER
      ? css`
          align-items: center;
          text-align: center;
        `
      : css`
          align-items: flex-start;
          /* Use the logical 'start' value rather than 'left' so the
             alignment flips correctly under RTL writing modes. 'left'
             would mis-align in RTL contexts. */
          text-align: start;
        `}
`;

export const IconSlot = styled.span<{ $size: EmptyStateSize }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  ${({ $size }) => {
    const tokens = EMPTY_STATE_SIZE_TOKENS[$size];
    return css`
      width: ${tokens.iconSize};
      height: ${tokens.iconSize};
      & > svg,
      & > .material-symbols-rounded {
        font-size: ${tokens.iconSize};
        width: ${tokens.iconSize};
        height: ${tokens.iconSize};
      }
    `;
  }}
  ${({ theme: { colors = {} } }) => css`
    color: ${colors.onSurfaceVariant ?? '#C7C4D7'};
    /* Slightly muted so the icon reads as decorative chrome, not the
       primary message — the title carries the meaning. */
    opacity: 0.7;
  `}
`;

export const Title = styled.p<{ $size: EmptyStateSize }>`
  margin: 0;
  ${({ $size, theme: { colors = {} } }) => {
    // At xs the title visually registers as a soft inline hint inside a
    // dense card / list — regular weight on the muted surface ink — so it
    // doesn't compete with the surrounding card content. At sm/md/lg the
    // title is the empty surface's headline and uses the standard bold +
    // onSurface treatment.
    const isHint = $size === EMPTY_STATE_SIZE_XS;
    return css`
      font-weight: ${isHint ? 400 : 600};
      font-size: ${EMPTY_STATE_SIZE_TOKENS[$size].titleFontSize};
      color: ${isHint
        ? colors.onSurfaceVariant ?? '#C7C4D7'
        : colors.onSurface ?? '#E4E1ED'};
    `;
  }}
`;

export const Description = styled.p<{ $size: EmptyStateSize }>`
  margin: 0;
  max-width: 48ch;
  ${({ $size }) => css`
    font-size: ${EMPTY_STATE_SIZE_TOKENS[$size].descriptionFontSize};
  `}
  ${({ theme: { colors = {} } }) => css`
    color: ${colors.onSurfaceVariant ?? '#C7C4D7'};
  `}
  line-height: 1.45;
`;

export const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
`;
